import { useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraText,
  FAIconName,
  FireflyButton,
  Text,
} from '@equip.health/ui';

import { UnsignedNoteItem } from '~/store/services/providerDashboard';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { CARD_CONTENT_MIN_HEIGHT, CARD_STYLES, EMPTY_STATE_COPY, PENDING_NOTES_DEFAULT_VISIBLE_COUNT } from '../constants';
import DashboardCardEmptyState from '../DashboardCardEmptyState';
import DashboardCardErrorState from '../DashboardCardErrorState';
import PendingNoteRow from './PendingNoteRow';
import PendingNotesSkeleton from './PendingNotesSkeleton';
import checkmarkIllustration from '../assets/checkmark-illustration.svg';

interface ProviderDashboardPendingNotesCardProps {
  notes?: UnsignedNoteItem[];
  isLoading?: boolean;
  isError?: boolean;
  onReload: () => void;
  isReloading?: boolean;
}

const ProviderDashboardPendingNotesCard = ({
  notes,
  isLoading = false,
  isError = false,
  onReload,
  isReloading = false,
}: ProviderDashboardPendingNotesCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const { trackEvent } = useAnalytics() ?? {};

  const noteCount = notes?.length ?? 0;
  const hasMoreThanDefault = noteCount > PENDING_NOTES_DEFAULT_VISIBLE_COUNT;
  const visibleNotes = showAll
    ? notes
    : notes?.slice(0, PENDING_NOTES_DEFAULT_VISIBLE_COUNT);

  const handleTrackShowAllEvent = (isShowingAll: boolean) => {
    const event = isShowingAll
      ? ProviderDashboardEvents.pendingNotesShowLessClicked
      : ProviderDashboardEvents.pendingNotesShowMoreClicked;
    trackEvent?.(event, {
      totalNotesCount: noteCount,
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
  };

  const renderContent = () => {
    if (isLoading) return <PendingNotesSkeleton />;

    if (isError) {
      return (
        <DashboardCardErrorState
          variant="stacked"
          onReload={onReload}
          isReloading={isReloading}
        />
      );
    }

    if (!isLoading && noteCount === 0) {
      return (
        <DashboardCardEmptyState
          icon={
            <ChakraImage src={checkmarkIllustration} alt="" width="40px" height="40px" />
          }
          title={EMPTY_STATE_COPY.pendingNotes.title}
          body={EMPTY_STATE_COPY.pendingNotes.body}
        />
      );
    }

    return (
      <>
        {visibleNotes?.map((note) => (
          <PendingNoteRow key={note.noteExternalId} note={note} />
        ))}

        {hasMoreThanDefault && (
          <ChakraFlex
            borderTop="1px solid"
            borderColor="neutral.line"
            padding="16px 0"
          >
            <FireflyButton
              id="pending-notes-show-more"
              aria-label={showAll ? 'Show less' : 'Show more'}
              onClick={() => {
                handleTrackShowAllEvent(showAll);
                setShowAll(!showAll);
              }}
              variant="tertiary"
              size="small"
              leftIcon={
                showAll
                  ? FAIconName.chevronUpRegular
                  : FAIconName.chevronDownRegular
              }
            >
              {showAll ? 'Show less' : 'Show more'}
            </FireflyButton>
          </ChakraFlex>
        )}
      </>
    );
  };

  return (
    <ChakraBox
      data-testid="provider-dashboard-pending-notes-card"
      {...CARD_STYLES}
      padding="22px 22px 8px"
    >
      <ChakraFlex alignItems="center" gap="10px" marginBottom="6px">
        <Text variant="h5">Pending notes</Text>
        {!isLoading && !isError && (
          <ChakraFlex
            alignItems="center"
            justifyContent="center"
            minWidth="24px"
            height="24px"
            padding="0 8px"
            borderRadius="full"
            bg="neutral.background.quaternary"
            data-testid="pending-notes-count-badge"
          >
            <ChakraText
              fontSize="13px"
              fontWeight="500"
              color="neutral.secondary"
            >
              {noteCount}
            </ChakraText>
          </ChakraFlex>
        )}
      </ChakraFlex>

      <ChakraFlex
        data-testid="pending-notes-content-area"
        direction="column"
        minHeight={CARD_CONTENT_MIN_HEIGHT.pendingNotes}
        justifyContent={
          isLoading || isError || noteCount === 0
            ? 'center'
            : 'flex-start'
        }
      >
        {renderContent()}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default ProviderDashboardPendingNotesCard;
